import { Box, Grid, InputBase, Stack } from '@mui/material';
import { SearchIcon } from '@react-pdf-viewer/search';
import { useEffect, useMemo, useState, useRef } from 'react';
import { FormProvider, useForm, FieldValues } from 'react-hook-form';
import { accountManagerAutoComplete, getWorkspaceUsers, IUserRole, IWorkspace } from 'wisestep-api';
import { WsButton, WsCheckboxList } from 'wisestep-base';

import WsCheckBox from '../../../../components/WsCheckBox/WsCheckBox';
import { warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { StyledFooter } from '../types';

import { ICheckBoxListOptions } from './AssignedRecruiters';

interface IUsersDataStatus {
  workspaceUsers: IWorkspace[];
  totalUsers: number;
}
interface IAccountManagersList {
  managersData?: IWorkspace[];
  onInput?: (data: FieldValues) => void;
  selectedManager?: IWorkspace[];
}

const AccountManagersList = ({ onInput, selectedManager }: IAccountManagersList) => {
  const methods = useForm();
  const { watch, setValue, reset } = methods;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [workspaceData, setWorkspaceData] = useState<IUsersDataStatus>({
    workspaceUsers: [],
    totalUsers: 0,
  });
  const selectedManagerIds = watch('managersList');
  const abortControllerRef = useRef(new AbortController());
  useEffect(() => {
    getInvitedUsers();
  }, [selectedManager]);
  const getInvitedUsers = async () => {
    try {
      const query = {
        roles: [IUserRole.ACCOUNT_MANAGER],
        statuses: ['all'],
        userIds: [],
        page: 0,
        size: 50,
      };
      const response = await getWorkspaceUsers(query);
      const workspace = response.data;
      if (workspace.workspaceUsers && selectedManager) {
        const managers: IWorkspace[] = response.data.workspaceUsers;
        const selectedManagers: IWorkspace[] | undefined = selectedManager;
        const uniqueManaers = removeDuplicatesFromArray(managers, selectedManagers, 'userId');
        setWorkspaceData({
          workspaceUsers: uniqueManaers,
          totalUsers: workspace.totalUsersCount,
        });
      } else {
        setWorkspaceData({
          workspaceUsers: workspace.workspaceUsers,
          totalUsers: workspace.totalUsersCount,
        });
      }
    } catch (e) {
      setWorkspaceData({
        workspaceUsers: [],
        totalUsers: 0,
      });
    }
  };
  const removeDuplicatesFromArray = (sourceArray, filterArray, property) => {
    return sourceArray.filter(
      (item) => !filterArray.some((filterItem) => filterItem[property] === item[property])
    );
  };
  const managersOptions: ICheckBoxListOptions = useMemo(() => {
    const temp = {};
    workspaceData?.workspaceUsers?.forEach((manager) => {
      manager?.userId && (temp[manager?.userId] = manager?.userName ?? manager?.userId);
    });

    return temp;
  }, [workspaceData]);

  const handleSelectEvents = (checked: boolean) =>
    checked ? setValue('managersList', Object.keys(managersOptions)) : setValue('managersList', []);

  const saveName = (data) => {
    if (data?.managersList?.length > 0) {
      onInput && onInput(data);
      reset();
    } else {
      setNotification(warning('Please select atleast one manager'));
    }
  };

  const onUserInputKey = async (searchText: string) => {
    if (searchText?.length > 2) {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const newAbortController = new AbortController();
      abortControllerRef.current = newAbortController;
      try {
        const response = await accountManagerAutoComplete(searchText, newAbortController.signal);
        const uniqueManaers = removeDuplicatesFromArray(response.data, selectedManager, 'userId');
        setWorkspaceData({
          workspaceUsers: uniqueManaers,
          totalUsers: response.data.length,
        });
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    }
    if (searchText.length === 0) {
      getInvitedUsers();
    }
  };

  return (
    <Box>
      <FormProvider {...methods}>
        <Grid container pl={2}>
          <Stack direction="row" spacing={1} alignItems={'center'}>
            <SearchIcon />
            <InputBase
              placeholder="search"
              name="searchtext"
              onChange={(e) => onUserInputKey(e.target.value)}
              fullWidth
            />
          </Stack>
        </Grid>

        <Stack spacing={1}>
          <Grid container pl={2}>
            <WsCheckBox
              name="selectAll"
              label="Select All Managers"
              bold={true}
              onClick={handleSelectEvents}
            />
          </Grid>

          <Grid container pl={2} pb={8.5}>
            <WsCheckboxList name="managersList" options={managersOptions} />
          </Grid>
        </Stack>
        <StyledFooter>
          <Grid container item justifyContent="space-between" py={1}>
            <WsButton variant="text" size="large">
              {`${selectedManagerIds?.length ?? 0} Selected`}
            </WsButton>

            <WsButton size="large" variant="contained" onClick={methods.handleSubmit(saveName)}>
              Save
            </WsButton>
          </Grid>
        </StyledFooter>
      </FormProvider>
    </Box>
  );
};

export default AccountManagersList;
